import React, { useRef } from 'react';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import { getTextWidth, useCombinedRefs } from 'common';
import TextInput, { TextInputProps } from 'components/TextInput';
import { TextwChipsList, TextwChipsListProps } from 'components/TextwChips';


const {
  FONT_SIZE,
  FONT_FAMILY,
} = Tokens;
const EMPTY_STRING = '';
const INPUT_PADDING = 0.25;
const TEXT_FONT = `${FONT_SIZE.FONT_SIZE_14} ${FONT_FAMILY.NOKIA_PURE_TEXT}`;
const resolveTextInputConfig = (config) => {
  const divWidth = config.empty ? 'calc(100% - 1rem)' : 'auto';
  let inputWidth = config.empty ? '100%' : `${getTextWidth(config.value, TEXT_FONT) + INPUT_PADDING}rem`;
  inputWidth = config.empty && config.placeholder ? `${getTextWidth(config.placeholder, TEXT_FONT) + INPUT_PADDING}rem` : inputWidth;
  return {
    style: {
      width: divWidth,
      maxWidth: config.empty && !config.lastInput ? '0.3125rem' : 'none',
      minHeight: '100%',
      display: 'inline-block',
    },
    inputProps: {
      style: {
        padding: '0',
        border: 'none',
        boxShadow: 'none',
        width: inputWidth,
      },
      ...config.inputProps
    }
  };
};

export interface ListProps extends Omit<Partial<TextwChipsListProps>, 'list' | 'contextMenu'> {
  contextMenu?: {
    event?: {},
    items: {
      label?: string,
      itemNode?: React.ReactNode,
      key?: string,
      content?: React.ReactNode,
      searchPattern?: string,
      onClick?: React.MouseEventHandler,
      submitChip?: boolean
    }[]
  },
}
export interface TextwChipsInputProps extends Omit<TextInputProps, 'value'> {
  /** The value of the field. */
  value?: string;
  /** The short hint displayed in the input when it is empty. Note: The native input element's `placeholder` attribute will be set using this prop. */
  placeholder?: string;
  /** Set `true`, if the field is the last one in its container. */
  lastInput?: boolean;
  /** The focus index. */
  focusedIndex?: number;
  /** The tab index. */
  tabIndex?: number;
  /** Properties for TextwChipsList. */
  listProps?: ListProps;
  /**  Attributes applied to the input element. Note: DOM attributes should be camelCased. */
  inputProps?: React.ComponentPropsWithRef<'input'>,
  /** @ignore */
  onFocus?: React.FocusEventHandler;
  /** @ignore */
  onBlur?: React.FocusEventHandler;
}

/** Internal component. Any other props than the `TextwChipsInput` props will be provided to the `TextInput` component. */
const TextwChipsInput = React.forwardRef<HTMLElement, TextwChipsInputProps>((props: TextwChipsInputProps, ref) => {
  const {
    value,
    placeholder,
    lastInput,
    inputProps,
    focusedIndex,
    tabIndex,
    onFocus,
    onBlur,
    listProps,
    ...otherProps
  } = props;
  const { ref: propsInputRef, ...restOfInputProps } = inputProps;
  const {
    contextMenu,
    triggerEl,
    listRef,
    menuProps,
    ...restOfListProps
  } = listProps || {};
  const innerRefEl = useRef(null);
  const combinedRefs = useCombinedRefs(innerRefEl, propsInputRef);

  const resolvedIndex = focusedIndex === undefined ? value.length > 0 ? 0 : -1 : 0;
  const resolvedProps = resolveTextInputConfig({
    lastInput,
    value,
    placeholder,
    empty: value === EMPTY_STRING,
    inputProps: {
      autoComplete: 'off',
      tabIndex: tabIndex === undefined ? resolvedIndex : tabIndex,
      'aria-label': value === EMPTY_STRING ? 'type some text' : value,
      ...restOfInputProps,
      ref: combinedRefs,
    },
  });
  return (
    <>
      <TextInput
        ref={ref}
        {...resolvedProps}
        value={value}
        placeholder={placeholder}
        {...otherProps}
      >
        {value}
      </TextInput>
      <TextwChipsList
        menuProps={menuProps}
        {...{
          list: contextMenu,
          triggerEl,
          listRef,
        }}
        {...restOfListProps}
      />
    </>
  );
});

TextwChipsInput.defaultProps = {
  value: EMPTY_STRING,
  placeholder: undefined,
  lastInput: false,
  inputProps: {},
  focusedIndex: undefined,
  tabIndex: undefined,
  listProps: undefined,
  onFocus: undefined,
  onBlur: undefined
};

TextwChipsInput.displayName = 'TextwChipsInput';
export default TextwChipsInput;
